/**
 * Presenza utente allineata a qcenter (tabella sessioni).
 * - Update a selezione tab (focus)
 * - Heartbeat ogni ~15s solo se questa tab detiene il claim
 * - Nello stesso heartbeat: consegna notifiche device online (tab leader)
 * - Claim condiviso via localStorage + cookie Domain=.hightek.it (cross-subdomain)
 * - Rilascio claim in chiusura → altre tab/app possono ereditare
 */
import { getPresenceTabId } from './presenceClaim';
import {
    preparePlatformDeviceOnlineNotifications,
    showPlatformDeviceOnlineNotifications,
} from './platformDeviceOnlineNotifications';
import { HEARTBEAT_INTERVAL_MS } from './deviceOnlineTiming';

export function startSessionPresence(options) {
    var apiClient = options.apiClient;
    var pageRoute = options.pageRoute;
    var qliveUrl = options.qliveUrl;
    var iconUrl = options.iconUrl;
    var workerUrl = options.workerUrl || (process.env.PUBLIC_URL || '') + '/sessionHeartbeatWorker.js';
    var INTERVAL_MS = options.intervalMs || HEARTBEAT_INTERVAL_MS;
    var STORAGE_KEY = 'qcenter-session-presence';
    var COOKIE_NAME = 'qcenter_presence';
    var CHANNEL_NAME = 'qcenter-session-presence';

    var tabId = getPresenceTabId();
    var channel = null;
    var worker = null;
    var intervalId = null;
    var takeoverTimer = null;
    var stopped = false;
    var lastSentAt = 0;

    preparePlatformDeviceOnlineNotifications({ apiClient: apiClient, qliveUrl: qliveUrl, iconUrl: iconUrl });

    function handleHeartbeatResponse(response) {
        if (!response || response.status !== 200 || !response.data) {
            return;
        }

        showPlatformDeviceOnlineNotifications(response.data.device_online_events, {
            apiClient: apiClient,
            qliveUrl: qliveUrl,
            iconUrl: iconUrl,
        });
    }

    function cookieDomainAttr() {
        var host = window.location.hostname;
        if (!host || host === 'localhost' || host === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
            return '';
        }
        var parts = host.split('.');
        if (parts.length < 2) {
            return '';
        }
        return '; Domain=.' + parts.slice(-2).join('.');
    }

    function readCookie(name) {
        var match = document.cookie.match(
            new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
        );
        if (!match) {
            return null;
        }
        try {
            return JSON.parse(decodeURIComponent(match[1]));
        } catch (e) {
            return null;
        }
    }

    function writeCookie(payload) {
        try {
            document.cookie =
                COOKIE_NAME + '=' + encodeURIComponent(JSON.stringify(payload)) +
                '; Path=/' + cookieDomainAttr() + '; SameSite=Lax; Max-Age=86400';
        } catch (e) {}
    }

    function readClaim() {
        var fromLs = null;
        var fromCookie = readCookie(COOKIE_NAME);
        try {
            fromLs = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        } catch (e) {
            fromLs = null;
        }

        if (fromLs && fromCookie) {
            return (fromCookie.ts || 0) >= (fromLs.ts || 0) ? fromCookie : fromLs;
        }
        return fromCookie || fromLs;
    }

    function persistClaim(payload) {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        } catch (e) {}
        writeCookie(payload);
        if (channel) {
            try { channel.postMessage(payload); } catch (e) {}
        }
    }

    function writeClaim() {
        persistClaim({
            type: 'claim',
            tabId: tabId,
            route: pageRoute,
            ts: Date.now()
        });
    }

    function releaseClaimIfMine() {
        var claim = readClaim();
        if (!claim || claim.type === 'release' || claim.tabId !== tabId) {
            return;
        }
        persistClaim({
            type: 'release',
            tabId: tabId,
            route: pageRoute,
            ts: Date.now()
        });
    }

    function isActiveTab() {
        var claim = readClaim();
        if (!claim || !claim.tabId || claim.type === 'release') {
            return false;
        }
        return claim.tabId === tabId;
    }

    function sendHeartbeat(force) {
        if (stopped || !apiClient) {
            return;
        }
        var now = Date.now();
        if (!force && lastSentAt && (now - lastSentAt) < (INTERVAL_MS - 2000)) {
            return;
        }
        lastSentAt = now;
        apiClient.post('/sessione/heartbeat', { route: pageRoute })
            .then(handleHeartbeatResponse)
            .catch(function () {});
    }

    function stopTimers() {
        if (worker) {
            try { worker.terminate(); } catch (e) {}
            worker = null;
        }
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
    }

    function startTimers() {
        stopTimers();
        if (typeof Worker !== 'undefined' && workerUrl) {
            try {
                worker = new Worker(workerUrl);
                try {
                    worker.postMessage({ type: 'configure', intervalMs: INTERVAL_MS });
                } catch (e) {}
                worker.onmessage = function () { onTick(); };
                worker.onerror = function () {
                    try { worker.terminate(); } catch (e) {}
                    worker = null;
                    if (!intervalId) {
                        intervalId = setInterval(onTick, INTERVAL_MS);
                    }
                };
                return;
            } catch (e) {
                worker = null;
            }
        }
        intervalId = setInterval(onTick, INTERVAL_MS);
    }

    function tryTakeover(releasedByTabId) {
        if (takeoverTimer) {
            clearTimeout(takeoverTimer);
        }
        takeoverTimer = setTimeout(function () {
            takeoverTimer = null;
            if (stopped) {
                return;
            }
            var claim = readClaim();
            if (claim && claim.type === 'claim' && claim.tabId && claim.tabId !== releasedByTabId) {
                return;
            }
            onPageSelected();
        }, 30 + Math.floor(Math.random() * 120));
    }

    function onTick() {
        if (stopped) {
            return;
        }
        var claim = readClaim();
        if (claim && claim.type === 'release' && claim.tabId !== tabId) {
            tryTakeover(claim.tabId);
            return;
        }
        // Leader morto senza pagehide (freeze/crash): claim stale → takeover
        if (
            claim &&
            claim.type === 'claim' &&
            claim.tabId &&
            claim.tabId !== tabId &&
            claim.ts &&
            (Date.now() - claim.ts) > 90000
        ) {
            tryTakeover(claim.tabId);
            return;
        }
        if (!isActiveTab()) {
            return;
        }
        writeClaim();
        sendHeartbeat();
    }

    function onPageSelected() {
        if (stopped) {
            return;
        }
        writeClaim();
        sendHeartbeat(true);
        startTimers();
    }

    function onClaimReleased(msg) {
        if (!msg || msg.type !== 'release' || msg.tabId === tabId) {
            return;
        }
        tryTakeover(msg.tabId);
    }

    function onFocus() {
        onPageSelected();
    }

    function onPageHide(event) {
        if (event.persisted) {
            return;
        }
        releaseClaimIfMine();
    }

    function onStorage(event) {
        if (event.key !== STORAGE_KEY || !event.newValue) {
            return;
        }
        try {
            onClaimReleased(JSON.parse(event.newValue));
        } catch (e) {}
    }

    try {
        if (typeof BroadcastChannel !== 'undefined') {
            channel = new BroadcastChannel(CHANNEL_NAME);
            channel.onmessage = function (event) {
                onClaimReleased(event.data);
            };
        }
    } catch (e) {
        channel = null;
    }

    window.addEventListener('focus', onFocus);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('storage', onStorage);

    // Anche senza focus: se non c'è un claim valido, questa tab lo prende
    // (pagina aperta in background / sotto altre tab / altro software).
    var initialClaim = readClaim();
    var claimMissing = !initialClaim || !initialClaim.tabId || initialClaim.type === 'release';
    var claimStale = initialClaim
        && initialClaim.type === 'claim'
        && initialClaim.ts
        && (Date.now() - initialClaim.ts) > 90000;

    if (document.hasFocus() || claimMissing || claimStale) {
        onPageSelected();
    } else {
        startTimers();
    }

    return function stopSessionPresence() {
        stopped = true;
        releaseClaimIfMine();
        stopTimers();
        if (takeoverTimer) {
            clearTimeout(takeoverTimer);
            takeoverTimer = null;
        }
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('pagehide', onPageHide);
        window.removeEventListener('storage', onStorage);
        if (channel) {
            try { channel.close(); } catch (e) {}
            channel = null;
        }
    };
}
